/**
 * RecipientFields — the shared name + email cluster (§3.4). The travel flows
 * label the name "Utas neve"; the general (assistance) template passes
 * `nameVariant="customer"` for the insurer-facing "Ügyfél neve" — a general
 * insurance card has a customer, not a passenger. Presentational: the parent
 * owns the useForm instance and passes `register`/`errors`; the field names
 * must exist on the parent schema.
 */

import type { FieldErrors, UseFormRegister } from 'react-hook-form';

import { useT } from '../../i18n';

export interface RecipientFieldValues {
  recipientName: string;
  recipientEmail: string;
}

export interface RecipientFieldsProps<T extends RecipientFieldValues> {
  register: UseFormRegister<T>;
  errors: FieldErrors<T>;
  /** Name label: 'traveler' (default, "Utas neve") or 'customer' ("Ügyfél neve"). */
  nameVariant?: 'traveler' | 'customer';
}

export function RecipientFields<T extends RecipientFieldValues>({
  register,
  errors,
  nameVariant = 'traveler',
}: RecipientFieldsProps<T>): JSX.Element {
  const t = useT();
  const nameError = errors.recipientName?.message as string | undefined;
  const emailError = errors.recipientEmail?.message as string | undefined;
  return (
    <>
      <div>
        <label htmlFor="recipientName" className="label">
          {t(nameVariant === 'customer' ? 'form.customerName' : 'form.travelerName')}
        </label>
        <input
          id="recipientName"
          {...register('recipientName' as never)}
          className="input mt-1"
          placeholder={t('form.travelerNamePlaceholder')}
          autoComplete="name"
        />
        {nameError ? <div className="text-xs text-red-600 mt-1">{nameError}</div> : null}
      </div>
      <div>
        <label htmlFor="recipientEmail" className="label">
          {t('form.recipientEmail')}
        </label>
        <input
          id="recipientEmail"
          type="email"
          {...register('recipientEmail' as never)}
          className="input mt-1"
          placeholder="anna@example.com"
          autoComplete="email"
          aria-describedby="recipientEmail-hint"
        />
        <p id="recipientEmail-hint" className="text-xs text-slate-500 mt-1">
          {t('form.recipientEmailHint')}
        </p>
        {emailError ? <div className="text-xs text-red-600 mt-1">{emailError}</div> : null}
      </div>
    </>
  );
}
